import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { FirebaseAuthentication, User } from '@capacitor-firebase/authentication';
import { firstValueFrom, Observable, ReplaySubject, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BasicResponse, FingerprintGroupSubscribeResponse, FingerprintTokenResponse, Group, GroupField, GroupFieldsResponse, GroupPrivacyConsentResponse, GroupPrivacyConsentUserResponse, GroupPrivacyResponse, GroupResponse, GroupsResponse, NeedsUpdateResponse, Transaction, UserGroupFieldsResponse, UserResponse } from './app.types';
import { AlertController, ModalController, NavController, ToastController } from '@ionic/angular/standalone';
import { Capacitor } from '@capacitor/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Preferences } from '@capacitor/preferences';
import { UserGroupDataResponse, UserPrivacyResponse } from './auth-form/auth-form.page';
import { addIcons } from 'ionicons';
import { alertCircleOutline } from 'ionicons/icons';
import { AuthNavPage } from './auth-nav/auth-nav.page';
import { initializeApp } from 'firebase/app';
import SHA256 from 'crypto-js/sha256'
import { AuthComponent } from './components/auth/auth.component';
import { App } from '@capacitor/app';
import { AppUpdatePage } from './app-update/app-update.page';
import { WelcomePage } from './welcome/welcome.page';
import { MessagingService } from './messaging.service';

export interface TempoUser {
  uid: string
  displayName: string
  email: string
  image: string
  selectedGroup?: Group
  unlockedGroups?: Group[]
  adminGroups?: Group[]
  emailVerified: boolean
}

/*
* Auth service for login
*/

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  
  private userSubject = new ReplaySubject<TempoUser | null>(1)
  private user: TempoUser
  public embGroup?: Group
  private messaging = inject(MessagingService)
  private settingUpUser = false
  private readonly manualLogoutKey = "manualLogout"

  private selectGroupSubscription: Subscription

  httpOptions = {
    headers: new HttpHeaders({
      'Authorization': 'Bearer 629ddb523040fcafa3abf9f4f99fb216'
    })
  }

  constructor(
    private readonly ngZone: NgZone,
    private toastController: ToastController,
    private alertController: AlertController,
    private modalController: ModalController,
    private navController: NavController,
    private http: HttpClient
  ) {

    addIcons({ alertCircleOutline })

    if (!Capacitor.isNativePlatform()) {
      console.log("Initializing firebase web")
      initializeApp(environment.firebase)
    }

    this.addListeners()

    
    this.selectGroupSubscription = this.messaging.selectGroupStateChange$.subscribe((g) => {
      this.setSelectedGroup(g, true)
    })
    
  }

  ngOnDestroy(): void {
    this.selectGroupSubscription.unsubscribe()
  }

  async addListeners() {

    /* Check for app update */
    if (Capacitor.getPlatform() != "web") {
      const info = await App.getInfo()
      const nures = await this.needsUpdate(info.build)
      if (nures.needsUpdate) {
        console.log("App needs update")
        const modal = await this.modalController.create({
          component: AppUpdatePage,
          componentProps: {
            workinprogress: nures.workinprogress,
            message: nures.message
          }
        })
        modal.present()
        return
      } else {
        console.log("No need for update")
      }
    }

    


    
      FirebaseAuthentication.removeAllListeners().then(async () => {
      FirebaseAuthentication.addListener('authStateChange', (change) => {
        if(this.settingUpUser) {
          console.log("already settingUpUser 1")
          return 
        }
        this.settingUpUser = true
        this.ngZone.run(async () => {
  
          if (change.user) {
            if (await this.isManualLogoutPending()) {
              console.log("Manual logout pending, ignoring restored native session")
              await FirebaseAuthentication.signOut()
              this.userSubject.next(null)
              this.settingUpUser = false
              return
            }
            console.log("authStateChange with user")
  
            await this.setupUser(change.user)
            this.userSubject.next(this.user)
          } else {
            console.log("authStateChange NO user")
            if (Capacitor.isNativePlatform()) {
              if (await this.isManualLogoutPending()) {
                console.log("Manual logout pending, skipping fingerprint login")
                this.userSubject.next(null)
              } else {
                const fptres = await this.getFingerprintToken()
                if (fptres.ctoken) {
                  console.log("fingerprint found")
                  await this.signInWithCustomToken(fptres.ctoken)
                } else {
                  console.log("No fingerprint found, navigating root /")
                  this.userSubject.next(null)
                  //this.navController.navigateRoot("/")
                }
              }
            } else {
              this.userSubject.next(null)
            }
  
          }
  
          this.settingUpUser = false
        })
      })
      

    })

    

    

    if (!environment.production && Capacitor.isNativePlatform()) {
      if(this.settingUpUser) {
        console.log("already settingUpUser 2")
        return
      }
      FirebaseAuthentication.getCurrentUser().then(async (change) => {
        console.log("getCurrentUser (support dev livereload)", change.user)
        if (change.user) {
          if (await this.isManualLogoutPending()) {
            console.log("Manual logout pending, ignoring current native user")
            await FirebaseAuthentication.signOut()
            this.userSubject.next(null)
            this.settingUpUser = false
            return
          }
          await this.setupUser(change.user)
          this.userSubject.next(this.user)
        } else {
          console.log("authStateChange NO user")
          if (Capacitor.isNativePlatform()) {
            if (await this.isManualLogoutPending()) {
              console.log("Manual logout pending, skipping fingerprint login")
              this.userSubject.next(null)
            } else {
              const fptres = await this.getFingerprintToken()
              if (fptres.ctoken) {
                console.log("fingerprint found")
                await this.signInWithCustomToken(fptres.ctoken)
              } else {
                console.log("No fingerprint found, navigating root /")
                this.userSubject.next(null)
                //this.navController.navigateRoot("/")
              }
            }
          } else {
            this.userSubject.next(null)
          }
        }

        this.settingUpUser = false
      })
    }
  }

  hiddenUid(uid: string) {
    return uid.substring(0, 8) + "...."
  }

  async updateUser() {
    const fbu = await FirebaseAuthentication.getCurrentUser()
    if (fbu.user) {
      await this.setupUser(fbu.user)
      this.userSubject.next(this.user)
    }

  }

  refreshUser() {
    this.userSubject.next(this.user)
  }

  private mergeGroup(groups: Group[], group?: Group | null) {
    if (!group) {
      return groups
    }

    const groupExists = groups.some((g) => g.gid === group.gid)
    if (!groupExists) {
      groups.push(group)
    }

    return groups
  }

  private findGroupById(groups: Group[], gid?: number | null) {
    if (!gid) {
      return null
    }

    return groups.find((g) => g.gid === gid) ?? null
  }

  private resolveStartupSelectedGroup(
    groups: Group[],
    savedGroup: Group | null,
    fingerprintGroup: Group | null,
    lastTransGid?: number | null
  ) {
    // Selection priority on startup:
    // 1. explicit invite/embed group
    // 2. fingerprint-matched group
    // 3. previously saved user choice
    // 4. backend last transaction group
    // 5. first unlocked group
    return this.embGroup
      ?? this.findGroupById(groups, fingerprintGroup?.gid)
      ?? savedGroup
      ?? this.findGroupById(groups, lastTransGid)
      ?? groups[0]
      ?? null
  }

  private mergeEmbedGroup(groups: Group[], embGroup?: Group) {
    if (!embGroup) {
      console.log("No embGroup set, using unlocked groups")
      return groups
    }

    console.log("Got embGroup", embGroup.name)
    return this.mergeGroup(groups, embGroup)
  }

  async applyPendingEmbedGroupForCurrentUser() {
    if (!Capacitor.isNativePlatform() || !this.user || !this.embGroup) {
      return false
    }

    const embGroup = this.embGroup
    console.log("Applying pending embGroup for active user", embGroup.name)

    await this.subscribeToGroup(embGroup.gid, this.user.uid)

    const ungres = await this.getUnlockedGroups(this.user.uid)
    this.user.unlockedGroups = this.mergeEmbedGroup(ungres.groups, embGroup)
    this.user.selectedGroup = embGroup

    await this.saveSelectedGroup(embGroup)
    await this.addUser(this.user)

    this.messaging.setSelectedGroup(this.user.selectedGroup)
    await this.messaging.subscribeToMainTopics(this.user.uid, this.user.unlockedGroups.map((g) => g.gid))
    await this.messaging.updateBadges(this.user.uid, this.user.selectedGroup.gid)

    this.userSubject.next(this.user)
    this.embGroup = null

    return true
  }

  async syncFingerprintGroupForCurrentUser() {
    if (!Capacitor.isNativePlatform() || !this.user) {
      return false
    }

    const fpg = await this.getFingerprintGroupSubscribe()
    if (!fpg.gid) {
      return false
    }

    const alreadySelected = this.user.selectedGroup?.gid === fpg.gid
    const alreadyUnlocked = !!this.findGroupById(this.user.unlockedGroups || [], fpg.gid)
    if (alreadySelected && alreadyUnlocked) {
      console.log("Fingerprint group already applied", fpg.gid)
      return false
    }

    // Vediamo qui se il reward è già stato assegnato o no, in caso di gruppo fingerprint che non è ancora stato assegnato all'utente (es. primo login da nuovo dispositivo)

    const gres = await this.groupById(fpg.gid)
    this.embGroup = gres.group
    console.log("Fingerprint group changed, scheduling embGroup", this.embGroup.name)

    return this.applyPendingEmbedGroupForCurrentUser()
  }

  async needsUpdate(build: string) {
    const url = "https://api.aitempo.online/needsUpdate/" + build
    return firstValueFrom(this.http.get<NeedsUpdateResponse>(url, this.httpOptions))
  }

  async showWelcomeIfNeeded() {
    const w = await Preferences.get({ key: "welcomeShown" })
    console.log("welcomeShown", JSON.stringify(w,null,2))
    return w.value == null
  }

  async setWelcomeShown() {
    await Preferences.set({
      key: "welcomeShown",
      value: "true"
    })
  }

  async generateFingerprint() {
    const res = await firstValueFrom(this.http.get<any>("https://api.ipify.org?format=json"))
    console.log("my ip", res.ip)
    
    // Generate alert to show the ip if app is in development, to let the developer know the value that will be hashed for fingerprinting (since in dev it will likely change often due to dynamic ips)
    //if (!environment.production) {
      // const alert = await this.alertController.create({
      //   header: 'IP Address',
      //   message: res.ip,
      //   buttons: ['OK']
      // })
      // await alert.present()
    //}

    //const json = JSON.stringify(data)
    const hash = SHA256(res.ip).toString()

    if (!environment.production) {
      // const t = await this.toastController.create({
      //   message: hash,
      //   duration: 4000
      // })
      // t.present()
    }


    return hash
  }

  async ptTransaction(ptt: Transaction) {
    const url = "https://api.aitempo.online/ptTransaction"
    return await firstValueFrom(this.http.post<BasicResponse>(url, ptt, this.httpOptions))
  }

  setEmbedGroupSubscribe(g: Group) {
    this.embGroup = g
    console.log("setEmbedGroup", g.name)
  }

  async signInWithCustomToken(token: string) {
    const result = await FirebaseAuthentication.signInWithCustomToken({
      token: token,
    })
    return result.user
  }

  public userExists() {
    return this.user != undefined
  }

  public async sendEmailVerification() {
    await FirebaseAuthentication.sendEmailVerification()
    console.log("Email sent")
  }

  public get authStateChange$(): Observable<TempoUser> {
    return this.userSubject.asObservable()
  }

  async saveSelectedGroup(g?: Group | null) {
    if (!g) {
      return
    }

    await Preferences.set({
      key: "selectedGroup",
      value: g.gid + ""
    })

  }

  async persistSelectedGroup() {
    await this.saveSelectedGroup(this.user?.selectedGroup)
  }

  async setSelectedGroup(g: Group, refresh: boolean = false) {
    if (!this.user) {
      return
    }

    this.user.selectedGroup = g
    this.messaging.setSelectedGroup(g)

    if (refresh) {
      this.userSubject.next(this.user)
    }

    await this.saveSelectedGroup(g)
    
    if (refresh) {
      await this.messaging.updateBadges(this.user.uid, this.user.selectedGroup.gid)
    }
    
  }

  public getUser() {
    return this.user
  }

  async subscribeToGroup(gid: number, uid: string) {
    const ptt: Transaction = {
      gid: gid,
      tid: 0,
      info: "Iscrizione al canale",
      amount: 0,
      uid: uid
    }
    await this.ptTransaction(ptt)
  }

  async groupById(gid: number) {
    const url = "https://api.aitempo.online/groupById/" + gid
    return firstValueFrom(this.http.get<GroupResponse>(url, this.httpOptions))
  }

  async getGroupPrivacy(gid: number) {
    const url = "https://api.aitempo.online/getGroupPrivacy/" + gid
    return firstValueFrom(this.http.get<GroupPrivacyResponse>(url, this.httpOptions))
  }

  async getGroupPrivacyConsent(gid: number) {
    const url = "https://api.aitempo.online/getGroupPrivacyConsent/" + gid
    return firstValueFrom(this.http.get<GroupPrivacyConsentResponse>(url, this.httpOptions))
  }

  async getGroupPrivacyConsentUser(firebaseUid: string, gid: number) {
    const url = "https://api.aitempo.online/getGroupPrivacyConsentUser/" + firebaseUid + "/" + gid
    return firstValueFrom(this.http.get<GroupPrivacyConsentUserResponse>(url, this.httpOptions))
  }

  getSelectedGroup() {
    return this.user.selectedGroup
  }

  async setupUser(user: User) {

    if (user) {
      await this.clearManualLogoutPending()
      console.log("User logged on firebase: " + user.email)

      this.user = {
        displayName: user.displayName ? user.displayName : user.email ? user.email.split("@")[0] : "User",
        email: user.email,
        image: user.photoUrl,
        uid: user.uid,
        emailVerified: user.emailVerified
      }




      if (!Capacitor.isNativePlatform()) {
        return
      }

      // const ures = await this.getDbUser(user.uid)
      // if (ures.user.gid == 0) {
      //   ures.user.gid = 1
      // }

      if (this.embGroup) {
        await this.subscribeToGroup(this.embGroup.gid, user.uid)
      }

      let fingerprintGroup: Group | null = null
      const fpg = await this.getFingerprintGroupSubscribe()
      if (fpg.gid != 0) {
        await this.subscribeToGroup(fpg.gid, user.uid)
        const g = await this.groupById(fpg.gid)
        fingerprintGroup = g.group
      }

      const ungres = await this.getUnlockedGroups(user.uid)

      this.user.unlockedGroups = this.mergeGroup(
        this.mergeEmbedGroup(ungres.groups, this.embGroup),
        fingerprintGroup
      )

      const savedGroup = await this.getSelectedGroupSaved()
      const selectedGroup = this.resolveStartupSelectedGroup(
        this.user.unlockedGroups,
        savedGroup,
        fingerprintGroup,
        ungres.lastTransGid
      )

      if (selectedGroup) {
        this.user.selectedGroup = selectedGroup
        await this.saveSelectedGroup(selectedGroup)
      }

      // console.log("selectedGroup", this.user.selectedGroup)
      // await this.saveSelectedGroup(this.user.selectedGroup)


      for (const g of this.user.unlockedGroups) {
        const upr = await this.getUserPrivacy(user.uid, g.custom_privacy ? g.gid : 1)

        let showPrivacy = false

        if (g.custom_privacy) {
          const gp = await this.getGroupPrivacy(g.gid)
          if (gp.gprivacy) {
            const gpc = await this.getGroupPrivacyConsent(g.gid)
            const gpcu = await this.getGroupPrivacyConsentUser(user.uid, g.gid)

            console.log(gpc)
            console.log(gpcu)

            const gpc_mandatory = gpc.consents.filter((c) => c.mandatory)
            const gpcu_consented = gpcu.consents.filter((c) => 
              c.value 
                //&& c.version == gp.gprivacy.version
              )
            console.log(g.name, "group mandatory", gpc_mandatory.length, "user mandatory", gpcu_consented.length)

            const gpc_mandatory_ids = gpc_mandatory.map((c) => c.gpcid)
            const gpcu_consented_ids = gpcu_consented.map((c) => c.gpcid)

            const gpc_mandatory_not_consented = gpc_mandatory_ids.filter((id) => !gpcu_consented_ids.includes(id))
            console.log(g.name, "mandatory not consented:", gpc_mandatory_not_consented.length, gpc_mandatory_not_consented)

            if (gpc_mandatory_not_consented.length > 0) {
              showPrivacy = true
            }
          }
        } else {
          const gfres = await this.getGroupFields(g.gid)
          console.log(g.name, " NO custom_privacy, groupFields:", gfres.groupFields.length)

          const gfures = await this.getUserGroupData(user.uid, g.gid)

          if (gfres.groupFields.length > 0 && !gfures.result && !g.consents_rule) {
            const rmodal = await this.modalController.create({
              component: AuthNavPage,
              componentProps: {
                groupFields: gfres.groupFields,
                group: g,
                firebaseUid: user.uid,
                fromMenu: false
              }
            })
            rmodal.present()
            await rmodal.onDidDismiss()
          }
        }


        if (!upr.result || showPrivacy) {
          console.log("Show user privacy:", g.name, showPrivacy)
          const gfres = await this.getGroupFields(g.gid)
          const gfures = await this.getUserGroupData(user.uid, g.gid)
          console.log(gfures)
          
          const rmodal = await this.modalController.create({
            component: AuthNavPage,
            componentProps: {
              groupFields: (gfures.result && gfures.result.data.groupFields) ? gfures.result.data.groupFields : gfres.groupFields,
              group: g,
              firebaseUid: user.uid,
              fromMenu: false
            }
          })
          rmodal.present()
          await rmodal.onDidDismiss()

          if (g.gid == 1) {
            const gf = await this.getUserGroupData(user.uid, 1)
            const gfnn = gf.result.data.groupFields.filter((gf: GroupField) => gf.label === "Nickname")
            if (gfnn.length > 0) {
              this.user.displayName = gfnn[0].value
            }
          }
        }
      }

      // if (ungres.lastTransGid != 0) {
      //   this.user.selectedGroup = this.user.unlockedGroups.filter((g) => g.gid === ungres.lastTransGid)[0]
      //   await this.saveSelectedGroup(this.user.selectedGroup)
      // }

      try {
        await this.addUser(this.user)
        this.embGroup = null

        // Check admin groups
        const agres = await this.getAdminGroups(user.uid)
        this.user.adminGroups = agres.groups
        this.messaging.setSelectedGroup(this.user.selectedGroup)

        this.enableNotifications()
      } catch (error) {
        console.log("updateUser error:", error)
        this.showToast("Si è verificato un errore, riprova. " + error.toString(), "alert-circle")
      }

      try {
        await this.modalController.dismiss({home:true})
      } catch (error) {
        console.log("no modal to dismiss")
      }
      if (this.user.emailVerified) {
        const sw = await this.showWelcomeIfNeeded()

        if(sw) {
          const wmodal = await this.modalController.create({
            component: WelcomePage
          })
          await wmodal.present()
        }
      }
      

    } else {
      console.log("User is NOT logged...")
    }
  }



  async getAdminGroups(firebaseUid: string) {
    const url = "https://api.aitempo.online/groupsByAdmin/" + firebaseUid

    return firstValueFrom(this.http.get<GroupsResponse>(url, this.httpOptions))
  }

  async getUserGroupData(firebaseUid: string, gid: number) {
    const url = "https://api.aitempo.online/getUserGroupData"

    const body = {
      firebaseUid: firebaseUid,
      gid: gid
    }

    console.log("calling: " + url)
    return firstValueFrom(this.http.post<UserGroupDataResponse>(url, body, this.httpOptions))

  }

  async getUserTrainingData(firebaseUid: string, tid: number) {
    const url = "https://api.aitempo.online/getUserTrainingData"

    const body = {
      firebaseUid: firebaseUid,
      tid: tid
    }

    console.log("calling: " + url)
    return firstValueFrom(this.http.post<UserGroupDataResponse>(url, body, this.httpOptions))

  }

  async enableNotifications() {
    await this.messaging.initializeMessaging(this.user.uid, this.user.selectedGroup.gid)
    await this.messaging.subscribeToMainTopics(this.user.uid, this.user.unlockedGroups.map((g) => g.gid))
    await this.messaging.updateBadges(this.user.uid, this.user.selectedGroup.gid)
  }

  async updateBadges() {
    this.messaging.updateBadges(this.user.uid, this.user.selectedGroup.gid)
  }

  async getFingerprintToken() {
    const fp = await this.generateFingerprint()
    const url = "https://api.aitempo.online/getFingerprintToken"
    const body = {
      fingerprint: fp
    }
    return firstValueFrom(this.http.post<FingerprintTokenResponse>(url, body, this.httpOptions))
  }

  async getFingerprintGroupSubscribe() {
    const fp = await this.generateFingerprint()
    const url = "https://api.aitempo.online/getFingerprintGroupSubscribe"
    const body = {
      fingerprint: fp,
      uid: this.user ? this.user.uid : null
    }
    return firstValueFrom(this.http.post<FingerprintGroupSubscribeResponse>(url, body, this.httpOptions))
  }

  async getGroupFields(gid: number) {
    var url = "https://api.aitempo.online/getGroupFields/" + gid
    return firstValueFrom(this.http.get<GroupFieldsResponse>(url, this.httpOptions))
  }

  async getServiceFields(gid: number) {
    var url = "https://api.aitempo.online/getServiceFields/" + gid
    return firstValueFrom(this.http.get<GroupFieldsResponse>(url, this.httpOptions))
  }

  async getCustomServiceFields(sid: number) {
    var url = "https://api.aitempo.online/getCustomServiceFields/" + sid
    return firstValueFrom(this.http.get<GroupFieldsResponse>(url, this.httpOptions))
  }

  async getUserServiceData(gid: number) {
    const url = "https://api.aitempo.online/getUserServiceData/"
    const body = {
      firebaseUid: this.user.uid,
      gid: gid
    }
    console.log("calling: " + url)
    return firstValueFrom(this.http.post<UserGroupFieldsResponse>(url, body, this.httpOptions))
  }

  async getUserServiceDataCustom(sid: number) {
    const url = "https://api.aitempo.online/getUserServiceDataCustom/"
    const body = {
      firebaseUid: this.user.uid,
      sid: sid
    }
    console.log("calling: " + url)
    return firstValueFrom(this.http.post<UserGroupFieldsResponse>(url, body, this.httpOptions))
  }



  async getSelectedGroupSaved() {
    const res = await Preferences.get({ key: "selectedGroup" })
    if (res.value) {
      const gid = Number(res.value)
      const gcheck = this.user.unlockedGroups.filter((g) => g.gid === gid)
      if (gcheck.length > 0) {
        return gcheck[0]
      }
      return null
    } else {
      return null
    }
  }

  async getUserPrivacy(firebaseUid: string, gid: number) {
    const url = "https://api.aitempo.online/getUserPrivacy/" + firebaseUid + "/" + gid
    return firstValueFrom(this.http.get<UserPrivacyResponse>(url, this.httpOptions))
  }

  async getUnlockedGroups(uid: string) {
    const url = "https://api.aitempo.online/unlockedGroups/" + uid
    return firstValueFrom(this.http.get<GroupsResponse>(url, this.httpOptions))
  }

  async getDbUser(firebaseUid: string) {
    const url = "https://api.aitempo.online/getUser/" + firebaseUid
    return firstValueFrom(this.http.get<UserResponse>(url, this.httpOptions))
  }

  async showToast(message: string, icon?: string) {
    const toast = await this.toastController.create({
      duration: 3500,
      message: message,
      icon: icon ? icon : "alert-circle-outline",
      position: "bottom"
    })
    toast.present()
  }



  async addUser(user: TempoUser) {
    const url = "https://api.aitempo.online/addUser"


    let body = {
      user: user
    }

    return await firstValueFrom(this.http.post<BasicResponse>(url, body, this.httpOptions))
  }

  async logout(showLogin: boolean = false) {

    if (Capacitor.getPlatform() != "web") {

      for (let g of this.user?.unlockedGroups || []) {
        //FCM.unsubscribeFrom({ topic: "group_" + g.gid })
      }

      if (this.user) {
        //FCM.unsubscribeFrom({ topic: "user_" + this.user.uid })
      }

      /*
      if (this.mainGroup) {
        await FCM.unsubscribeFrom({ topic: "group_" + this.mainGroup.gid })
      }
 
      if (this.selectedGroup) {
        await FCM.unsubscribeFrom({ topic: "group_" + this.selectedGroup.gid })
      }
      */
    }

    //await this.removeSelectedGroupSaved()


    try {
      await this.setManualLogoutPending()
      await this.removeSelectedGroupSaved()
      this.user = null
      this.embGroup = null
      this.userSubject.next(null)
      await FirebaseAuthentication.signOut()

      console.log("Signout navigation auth or appstore")
      if (Capacitor.isNativePlatform()) {
        if (showLogin) {
          const modal = await this.modalController.create({
            component: AuthComponent
          })
          await modal.present()
          const { data } = await modal.onDidDismiss()
          if (data.home) {
            this.navController.navigateRoot("/")
          }
        }
        //this.navController.navigateRoot("auth")
      } else {
        this.navController.navigateRoot("appstore")
      }

    }
    finally {
      console.log("logout")
    }



    //await this.navController.navigateRoot("auth-nav")
    //await signOut(this.auth)
    //await GoogleAuth.signOut()
  }

  async deleteUser() {
    try {

      for (const g of this.user.unlockedGroups) {
        await this.leaveGroup(g.gid)
      }
      await this.removeSelectedGroupSaved()

      this.user = null

      await FirebaseAuthentication.signOut()
      await FirebaseAuthentication.deleteUser()

      this.userSubject.next(null)

    } catch (error) {
      console.log("deleteUser", error)
      this.showToast("Si è verificato un errore, riprova. " + error.toString(), "alert-circle-outline")
    }

  }

  async leaveGroup(gid: number) {
    const url = "https://api.aitempo.online/leaveSelectedGroup"
    const body = {
      uid: this.user.uid,
      gid: gid
    }

    await firstValueFrom(this.http.post(url, body, this.httpOptions))
  }

  async leaveSelectedGroup() {
    var url = "https://api.aitempo.online/leaveSelectedGroup"
    const body = {
      uid: this.user.uid,
      gid: this.user.selectedGroup.gid
    }

    console.log("calling: " + url)
    let p: Promise<BasicResponse> = new Promise((resolve, reject) => {
      this.http.post(url, body, this.httpOptions).toPromise().then((data: BasicResponse) => {
        if (data) {
          resolve(data)
        } else {
          reject(data)
        }
      })
    })

    return p
  }

  async removeSelectedGroupSaved() {
    await Preferences.remove({
      key: "selectedGroup"
    })
  }

  async isManualLogoutPending() {
    const res = await Preferences.get({ key: this.manualLogoutKey })
    return res.value === "true"
  }

  async clearManualLogoutPending() {
    await Preferences.remove({
      key: this.manualLogoutKey
    })
  }

  async setManualLogoutPending() {
    await Preferences.set({
      key: this.manualLogoutKey,
      value: "true"
    })
  }
}
